import React from 'react';
import styled from '@emotion/styled';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import DistributionBar from './DistributionBar';
import ShareBar from './ShareBar';
import { useTranslation } from '../../hooks/useTranslation';

/**
 * HomeworkView — the wall's picture of the room (#693): before the Kuutio,
 * what the ennakkotehtävät said; after it, what changed. One section at a
 * time, big enough to read from the back; the facilitator steps through
 * them and talks. Pure: the picture, the tab and the section come in as
 * props; HomeworkScreen fetches and handles the keys.
 */
const Page = styled.div`min-height: 100vh; background: #0b0d12; color: #e8ecf2; padding: 2.5rem 3rem 5rem; display: flex; flex-direction: column; gap: 2rem;`;
const Top = styled.header`display: flex; align-items: baseline; justify-content: space-between; gap: 2rem; flex-wrap: wrap;`;
const Title = styled.h1`margin: 0; font-size: 2rem; font-weight: 700; small { display: block; font-size: 0.9rem; font-weight: 400; color: #7b8794; margin-top: 0.2rem; }`;
const Tabs = styled.div`display: flex; gap: 0.5rem;`;
const Tab = styled.button`
  all: unset; cursor: pointer; padding: 0.55rem 1.1rem; border-radius: 999px; font-weight: 600; font-size: 1rem;
  border: 1px solid ${(p) => (p.$on ? '#8ec1f0' : '#2f3745')}; color: ${(p) => (p.$on ? '#0b1020' : '#c9cbd6')}; background: ${(p) => (p.$on ? '#8ec1f0' : 'transparent')};
`;
const Section = styled.section`display: flex; flex-direction: column; gap: 1.4rem; max-width: 1400px; width: 100%; margin: 0 auto; flex: 1;`;
const H2 = styled.h2`margin: 0; font-size: 1.6rem; font-weight: 700; color: #e8ecf2; span { color: #7b8794; font-weight: 400; font-size: 1rem; margin-left: 0.8rem; }`;
const Lead = styled.p`margin: 0; font-size: 1.15rem; color: #98a2b3; line-height: 1.5; max-width: 60ch;`;
const Numbers = styled.div`display: grid; grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr)); gap: 1.5rem;`;
const Num = styled.div`background: #141821; border: 1px solid #232a36; border-radius: 14px; padding: 1.6rem; strong { display: block; font-size: 3.4rem; line-height: 1; font-variant-numeric: tabular-nums; } span { display: block; margin-top: 0.6rem; color: #98a2b3; font-size: 1.05rem; }`;
const Stack = styled.div`display: flex; flex-direction: column; gap: 1.6rem;`;
const List = styled.div`display: flex; flex-direction: column; gap: 0.9rem;`;
const Group = styled.div`display: grid; grid-template-columns: 4rem minmax(0, 1fr) auto; gap: 1.2rem; align-items: center; background: #141821; border: 1px solid #232a36; border-radius: 12px; padding: 1rem 1.3rem; font-size: 1.2rem;
  strong { font-size: 2rem; font-variant-numeric: tabular-nums; } small { display: block; color: #7b8794; font-size: 0.9rem; margin-top: 0.15rem; }
  em { font-style: normal; font-size: 0.85rem; padding: 0.25rem 0.6rem; border-radius: 999px; border: 1px solid #3fbf8f; color: #3fbf8f; }`;
const Empty = styled.p`margin: 0; color: #7b8794; font-size: 1.2rem;`;
const Foot = styled.footer`display: flex; align-items: center; justify-content: space-between; color: #7b8794; font-size: 0.95rem; max-width: 1400px; width: 100%; margin: 0 auto;`;
const Nav = styled.button`all: unset; cursor: pointer; display: inline-flex; align-items: center; gap: 0.3rem; padding: 0.5rem 0.8rem; border-radius: 8px; border: 1px solid #2f3745; color: #c9cbd6; &:disabled { opacity: 0.35; cursor: default; } &:hover:not(:disabled) { border-color: #8ec1f0; }`;
const Dots = styled.div`display: flex; gap: 0.4rem; i { width: 0.5rem; height: 0.5rem; border-radius: 999px; background: #2f3745; } i.on { background: #8ec1f0; }`;
const Legend = styled.div`display: flex; gap: 1.2rem; flex-wrap: wrap; color: #98a2b3; font-size: 0.95rem; i { display: inline-block; width: 0.9rem; height: 0.9rem; border-radius: 3px; margin-right: 0.4rem; vertical-align: -1px; }`;

export const BEFORE_SECTIONS = ['done', 'selfAssessment', 'divided', 'claims', 'target', 'painPoints', 'peilaus'];
export const AFTER_SECTIONS = ['done', 'selfAssessment'];
const isEmpty = (n) => !n;

export default function HomeworkView({ data, title = '', tab = 'before', section = 0, onTab, onSection }) {
  const { t } = useTranslation();
  const sections = tab === 'before' ? BEFORE_SECTIONS : AFTER_SECTIONS;
  const key = sections[Math.min(section, sections.length - 1)];
  const b = data?.before; const a = data?.after;
  const three = (o) => [{ key: 'agree', count: o.agree }, { key: 'unsure', count: o.unsure }, { key: 'disagree', count: o.disagree }];
  const stanceLegend = (
    <Legend>
      <span><i style={{ background: '#3fbf8f' }} />{t('homework.agree')}</span>
      <span><i style={{ background: '#c9cbd6' }} />{t('homework.unsure')}</span>
      <span><i style={{ background: '#e05a5a' }} />{t('homework.disagree')}</span>
    </Legend>
  );
  const scaleLegend = (
    <Legend>
      {[1, 2, 3, 4, 5].map((k) => <span key={k}><i style={{ background: { 1: '#e05a5a', 2: '#e9a06a', 3: '#c9cbd6', 4: '#7fc8a9', 5: '#3fbf8f' }[k] }} />{k} · {t(`me.selfAssessment.scale.${k}`)}</span>)}
      <span><i style={{ background: '#3a4457' }} />{t('me.selfAssessment.unknown')}</span>
    </Legend>
  );

  let body = null;
  if (!data) body = <Empty>{t('me.loading')}</Empty>;
  else if (tab === 'before' && key === 'done') body = (
    <>
      <H2>{t('homework.doneTitle')}</H2>
      <Lead>{t('homework.doneLead')}</Lead>
      <Numbers>
        <Num><strong>{data.invited}</strong><span>{t('homework.invited')}</span></Num>
        <Num><strong>{b.done.selfAssessment}</strong><span>{t('homework.didSelfAssessment')}</span></Num>
        <Num><strong>{b.done.kysely}</strong><span>{t('homework.didKysely')}</span></Num>
        <Num><strong>{b.done.painPoints}</strong><span>{t('homework.didPainPoints', { count: b.painPoints.total })}</span></Num>
      </Numbers>
    </>
  );
  else if (tab === 'before' && key === 'selfAssessment') body = (
    <>
      <H2>{t('homework.selfAssessmentTitle')}<span>{t('homework.answered', { count: b.selfAssessment.participants })}</span></H2>
      {isEmpty(b.selfAssessment.participants) ? <Empty>{t('homework.empty')}</Empty> : (
        <Stack>{scaleLegend}{b.selfAssessment.items.map((it) => (
          <DistributionBar key={it.id} text={t(`me.selfAssessment.items.${it.id}`)} shares={it.shares} counts={it.counts} mean={it.mean} meanLabel={t('homework.mean')} />
        ))}</Stack>
      )}
    </>
  );
  else if (tab === 'before' && key === 'divided') body = (
    <>
      <H2>{t('homework.dividedTitle')}<span>{t('homework.answered', { count: b.kysely.participants })}</span></H2>
      <Lead>{t('homework.dividedLead')}</Lead>
      {isEmpty(b.kysely.participants) ? <Empty>{t('homework.empty')}</Empty> : (
        <Stack>{stanceLegend}{b.kysely.stance.map((o) => <ShareBar key={o.id} text={o.text} source={o.source} total={b.kysely.participants} parts={three(o)} size="1.3rem" />)}</Stack>
      )}
    </>
  );
  else if (tab === 'before' && key === 'claims') body = (
    <>
      <H2>{t('homework.claimsTitle')}<span>{t('homework.answered', { count: b.kysely.participants })}</span></H2>
      {isEmpty(b.kysely.participants) ? <Empty>{t('homework.empty')}</Empty> : (
        <Stack>{b.kysely.claims.map((o) => <ShareBar key={o.id} text={o.text} source={o.source} total={b.kysely.participants} parts={[{ key: 'agree', count: o.agree }]} size="1.3rem" />)}</Stack>
      )}
    </>
  );
  else if (tab === 'before' && key === 'target') body = (
    <>
      <H2>{t('homework.targetTitle')}<span>{t('homework.answered', { count: b.kysely.participants })}</span></H2>
      {isEmpty(b.kysely.participants) ? <Empty>{t('homework.empty')}</Empty> : (
        <Stack>
          {b.kysely.sockets.map((o) => <ShareBar key={o.id} text={o.text} total={b.kysely.participants} parts={[{ key: 'agree', count: o.agree, color: '#8ec1f0' }]} size="1.3rem" />)}
          <H2 style={{ fontSize: '1.25rem', marginTop: '0.5rem' }}>{t('homework.expectTitle')}</H2>
          {b.kysely.expect.map((o) => <ShareBar key={o.id} text={o.text} total={b.kysely.participants} parts={[{ key: 'pick', count: o.pick, color: '#f2c14e' }]} size="1.15rem" />)}
        </Stack>
      )}
    </>
  );
  else if (tab === 'before' && key === 'painPoints') body = (
    <>
      <H2>{t('homework.painPointsTitle')}<span>{t('homework.painPointsCount', { count: b.painPoints.total, people: b.painPoints.people })}</span></H2>
      {isEmpty(b.painPoints.total) ? <Empty>{t('homework.empty')}</Empty> : b.painPoints.groups.length === 0 ? (
        <List>{b.painPoints.sentences.slice(0, 12).map((s, i) => <Group key={i}><strong>·</strong><div>{s}</div><span /></Group>)}</List>
      ) : (
        <List>
          {b.painPoints.groups.map((g) => (
            <Group key={g.id}><strong>{g.size}</strong><div>{g.title}{g.scenarioTitle && <small>{g.scenarioTitle}</small>}</div>{g.promoted ? <em>{t('homework.promoted')}</em> : <span />}</Group>
          ))}
          {b.painPoints.ungrouped > 0 && <Empty>{t('homework.ungrouped', { count: b.painPoints.ungrouped })}</Empty>}
        </List>
      )}
    </>
  );
  else if (tab === 'before' && key === 'peilaus') body = (
    <>
      <H2>{t('homework.peilausTitle')}<span>{t('homework.mirrorsAnswered', { count: b.peilaus.answered })}</span></H2>
      <Lead>{t('homework.peilausLead')}</Lead>
      {isEmpty(b.peilaus.answered) ? <Empty>{t('homework.empty')}</Empty> : (
        <Stack>
          <Legend><span><i style={{ background: '#3fbf8f' }} />{t('homework.holds')}</span><span><i style={{ background: '#c9cbd6' }} />{t('homework.unsure')}</span><span><i style={{ background: '#e05a5a' }} />{t('homework.notHere')}</span></Legend>
          <ShareBar text={t('homework.allMirrors')} total={b.peilaus.answered} parts={[{ key: 'holds', count: b.peilaus.holds }, { key: 'unsure', count: b.peilaus.unsure }, { key: 'not_here', count: b.peilaus.not_here }]} size="1.3rem" />
          {b.peilaus.sources.map((s) => <ShareBar key={s.source} text={s.source || t('homework.unknownSource')} source={s.claims[0] || null} total={s.holds + s.unsure + s.not_here} parts={[{ key: 'holds', count: s.holds }, { key: 'unsure', count: s.unsure }, { key: 'not_here', count: s.not_here }]} />)}
        </Stack>
      )}
    </>
  );
  else if (tab === 'after' && key === 'done') body = (
    <>
      <H2>{t('homework.afterDoneTitle')}</H2>
      <Numbers>
        <Num><strong>{b.selfAssessment.participants}</strong><span>{t('homework.didSelfAssessmentBefore')}</span></Num>
        <Num><strong>{a.selfAssessment.participants}</strong><span>{t('homework.didSelfAssessmentAfter')}</span></Num>
      </Numbers>
      {isEmpty(a.selfAssessment.participants) && <Empty>{t('homework.noPost')}</Empty>}
    </>
  );
  else if (tab === 'after' && key === 'selfAssessment') body = (
    <>
      <H2>{t('homework.changeTitle')}<span>{t('homework.beforeAfter', { before: b.selfAssessment.participants, after: a.selfAssessment.participants })}</span></H2>
      {isEmpty(a.selfAssessment.participants) ? <Empty>{t('homework.noPost')}</Empty> : (
        <Stack>{scaleLegend}{a.selfAssessment.items.map((post) => {
          const pre = b.selfAssessment.items.find((x) => x.id === post.id);
          const delta = pre && pre.mean !== null && post.mean !== null ? post.mean - pre.mean : null;
          return (
            <div key={post.id}>
              <DistributionBar text={t(`me.selfAssessment.items.${post.id}`)} shares={pre?.shares || {}} counts={pre?.counts || null} mean={pre?.mean ?? null} meanLabel={t('homework.before')} height="1.4rem" />
              <div style={{ marginTop: '0.4rem' }}>
                <DistributionBar text="" shares={post.shares} counts={post.counts} mean={post.mean} meanLabel={delta === null ? t('homework.after') : `${t('homework.after')} ${delta >= 0 ? '+' : ''}${delta.toFixed(1)}`} height="1.4rem" size="0" />
              </div>
            </div>
          );
        })}</Stack>
      )}
    </>
  );

  return (
    <Page>
      <Top>
        <Title>{t('homework.title')}<small>{title}</small></Title>
        <Tabs>
          <Tab $on={tab === 'before'} onClick={() => onTab?.('before')}>{t('homework.tabBefore')}</Tab>
          <Tab $on={tab === 'after'} onClick={() => onTab?.('after')}>{t('homework.tabAfter')}</Tab>
        </Tabs>
      </Top>
      <Section>{body}</Section>
      <Foot>
        <Nav disabled={section <= 0} onClick={() => onSection?.(section - 1)}><ChevronLeft size={16} /> {t('homework.prev')}</Nav>
        <Dots>{sections.map((s, i) => <i key={s} className={i === section ? 'on' : ''} />)}</Dots>
        <Nav disabled={section >= sections.length - 1} onClick={() => onSection?.(section + 1)}>{t('homework.next')} <ChevronRight size={16} /></Nav>
      </Foot>
    </Page>
  );
}
